<template>
    <v-container class="px-5">
        <v-row>
            <v-col>
                <h4>App Installs</h4>
                <p class="text-caption text-grey">
                    Every Fleet, Go and Driver app install across all customers, one row per phone and user.
                    Usage is what each app reports about itself, so builds older than this feature show no data.
                </p>
            </v-col>
        </v-row>

        <!-- Summary -->
        <v-row dense>
            <v-col v-for="tile in tiles" :key="tile.label" cols="6" md="2">
                <v-card elevation="2" class="pa-4 h-100" :link="!!tile.status"
                        v-on="tile.status ? {click: () => setStatus(tile.status)} : {}">
                    <div class="text-caption text-medium-emphasis">{{ tile.label }}</div>
                    <div class="text-h5 font-weight-bold" :class="tile.color ? `text-${tile.color}` : ''">
                        {{ summaryLoading ? '-' : tile.value }}
                    </div>
                    <div class="text-caption text-grey">{{ tile.hint }}</div>
                </v-card>
            </v-col>
        </v-row>
        <v-row v-if="summary" dense class="mt-1">
            <v-col class="d-flex flex-wrap ga-2 align-center">
                <span class="text-caption text-medium-emphasis mr-1">By app</span>
                <v-chip v-for="row in summary.by_app" :key="row.app" size="small" variant="tonal"
                        :color="APPS[row.app]?.color" @click="setApp(row.app)">
                    {{ APPS[row.app]?.label || row.app }}: {{ row.count }}
                </v-chip>
                <span class="text-caption text-medium-emphasis ml-3 mr-1">By platform</span>
                <v-chip v-for="row in summary.by_platform" :key="row.platform" size="small" variant="tonal"
                        :prepend-icon="platformIcon(row.platform)" @click="setPlatform(row.platform)">
                    {{ row.count }}
                </v-chip>
            </v-col>
        </v-row>

        <!-- Filters -->
        <v-row dense class="mt-2">
            <v-col cols="12" md="3">
                <v-text-field v-model="search" density="compact" label="Search email, company, model, phone ID or IP"
                              prepend-inner-icon="mdi-magnify" clearable variant="outlined" hide-details
                              @update:model-value="debouncedFetch"/>
            </v-col>
            <v-col cols="6" md="2">
                <v-select v-model="app" :items="appOptions" label="App" density="compact"
                          variant="outlined" hide-details clearable @update:model-value="refresh"/>
            </v-col>
            <v-col cols="6" md="2">
                <v-select v-model="platform" :items="platformOptions" label="Platform" density="compact"
                          variant="outlined" hide-details clearable @update:model-value="refresh"/>
            </v-col>
            <v-col cols="6" md="2">
                <v-select v-model="status" :items="statusOptions" label="Status" density="compact"
                          variant="outlined" hide-details clearable @update:model-value="resetFetch"/>
            </v-col>
            <v-col cols="6" md="2">
                <v-select v-model="version" :items="versionOptions" label="Version" density="compact"
                          variant="outlined" hide-details clearable @update:model-value="refresh"/>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col class="d-flex flex-wrap ga-4 align-center">
                <v-switch v-model="includeInternal" label="Include store review accounts" density="compact"
                          color="primary" hide-details @update:model-value="refresh"/>
                <v-switch v-model="includeDeleted" label="Include deleted users" density="compact"
                          color="primary" hide-details @update:model-value="refresh"/>
                <v-chip v-if="companyId" closable size="small" @click:close="clearScope('company_id')">
                    Company #{{ companyId }}
                </v-chip>
                <v-chip v-if="userId" closable size="small" @click:close="clearScope('user_id')">
                    User #{{ userId }}
                </v-chip>
            </v-col>
        </v-row>

        <!-- List -->
        <v-row>
            <v-col>
                <v-data-table-server v-model:items-per-page="perPage" v-model:page="page"
                                     :headers="headers" :items="installs" :items-length="totalItems"
                                     :loading="loading" :items-per-page-options="[25, 50, 100]"
                                     hover @update:options="onOptions" @click:row="openRow">
                    <template v-slot:item.install_id="{ item }">
                        <span class="text-no-wrap">
                            <code>{{ shortId(item.install_id) }}</code>
                            <v-btn icon size="x-small" variant="text" @click.stop="copyToClipboard(item.install_id)">
                                <v-icon size="14">mdi-content-copy</v-icon>
                                <v-tooltip activator="parent" location="top">Copy full phone ID</v-tooltip>
                            </v-btn>
                        </span>
                    </template>
                    <template v-slot:item.phone="{ item }">
                        <span class="text-no-wrap">
                            <v-icon size="16" class="mr-1">{{ platformIcon(item.platform) }}</v-icon>
                            {{ phoneType(item) }}
                        </span>
                    </template>
                    <template v-slot:item.user="{ item }">
                        <div v-if="item.user">
                            {{ item.user.email }}
                            <v-chip v-if="item.user.deleted" size="x-small" color="error" variant="tonal" class="ml-1">deleted</v-chip>
                            <div class="text-caption text-grey">{{ item.user.name }}</div>
                        </div>
                        <span v-else class="text-grey">-</span>
                    </template>
                    <template v-slot:item.company="{ item }">
                        <span v-if="item.company">
                            {{ item.company.name }}
                            <v-chip v-if="item.company.deleted" size="x-small" color="error" variant="tonal" class="ml-1">deleted</v-chip>
                        </span>
                        <span v-else class="text-grey">-</span>
                    </template>
                    <template v-slot:item.app="{ item }">
                        <v-chip size="x-small" variant="tonal" :color="APPS[item.app]?.color">
                            {{ APPS[item.app]?.label || item.app }}
                        </v-chip>
                    </template>
                    <template v-slot:item.version="{ item }">
                        <span class="text-no-wrap">{{ versionLabel(item) }}</span>
                    </template>
                    <template v-slot:item.installed="{ item }">
                        <span class="text-no-wrap">{{ formatDate(item.installed_at) }}</span>
                        <div v-if="!item.installed_at_reported" class="text-caption text-grey">first seen</div>
                    </template>
                    <template v-slot:item.last_seen_at="{ item }">
                        <span class="text-no-wrap">{{ relativeTime(item.last_seen_at) }}</span>
                        <div v-if="item.last_ip" class="text-caption text-grey text-no-wrap">{{ item.last_ip }}</div>
                        <v-tooltip activator="parent" location="top">{{ formatDateTime(item.last_seen_at) }}</v-tooltip>
                    </template>
                    <template v-slot:item.status="{ item }">
                        <v-chip size="small" variant="tonal" :color="STATUS[item.status]?.color">
                            {{ STATUS[item.status]?.label || item.status }}
                            <v-tooltip activator="parent" location="top">{{ statusHint(item) }}</v-tooltip>
                        </v-chip>
                        <div v-if="!item.last_usage_date" class="text-caption text-grey">no usage data</div>
                    </template>
                </v-data-table-server>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {ref, computed} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {send} from '@/composables/apiClient'
import {useClipboard} from '@/composables/clipboard'
import {useSnackbarStore} from '@/stores/snackBarStore'
import {
    STATUS, APPS, platformIcon, phoneType, versionLabel, shortId,
    formatDate, formatDateTime, relativeTime,
} from '@/components/cards/appInstalls/appInstallFormat'

const route = useRoute()
const router = useRouter()
const snackbar = useSnackbarStore()
const {copyToClipboard} = useClipboard()

const loading = ref(false)
const summaryLoading = ref(false)
const installs = ref([])
const summary = ref(null)
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(25)
const sortBy = ref(null)

const search = ref('')
const app = ref(route.query.app || null)
const platform = ref(route.query.platform || null)
const status = ref(route.query.status || null)
const version = ref(null)
const companyId = ref(Number(route.query.company_id) || null)
const userId = ref(Number(route.query.user_id) || null)
// Opened for one company or user: show everything they have, including a
// deleted account or a store review company, which are hidden by default.
const scoped = !!(companyId.value || userId.value)
const includeInternal = ref(scoped)
const includeDeleted = ref(scoped)

let debounceTimer = null

const appOptions = Object.entries(APPS).map(([value, a]) => ({title: a.label, value}))
const platformOptions = [{title: 'Android', value: 'android'}, {title: 'iOS', value: 'ios'}]
const statusOptions = Object.entries(STATUS).map(([value, s]) => ({title: s.label, value}))
const versionOptions = computed(() => {
    const seen = new Set()
    return (summary.value?.by_version || [])
        .filter((v) => v.app_version && (!app.value || v.app === app.value))
        .map((v) => v.app_version)
        .filter((v) => !seen.has(v) && seen.add(v))
        .sort((a, b) => b.localeCompare(a, undefined, {numeric: true}))
})

const headers = [
    {title: 'Phone ID', key: 'install_id', sortable: false},
    {title: 'Phone', key: 'phone', sortable: false},
    {title: 'Email', key: 'user', sortable: true},
    {title: 'Company', key: 'company', sortable: true},
    {title: 'App', key: 'app', sortable: false},
    {title: 'Version', key: 'version', sortable: true},
    {title: 'Installed', key: 'installed', sortable: true},
    {title: 'Last seen', key: 'last_seen_at', sortable: true},
    {title: 'Status', key: 'status', sortable: false},
]
const SORT_KEYS = {user: 'email', company: 'company_name', version: 'app_version', installed: 'installed_at', last_seen_at: 'last_seen_at'}

const tiles = computed(() => {
    const s = summary.value
    const by = s?.by_status || {}
    return [
        {label: 'Installs', value: s?.total ?? 0, hint: 'matching the filters'},
        {label: 'Active', value: by.active ?? 0, hint: STATUS.active.hint, color: 'success', status: 'active'},
        {label: 'Seen in 7 days', value: s?.seen_7d ?? 0, hint: 'made any request'},
        {label: 'Seen in 30 days', value: s?.seen_30d ?? 0, hint: 'made any request'},
        {label: 'New this month', value: s?.new_this_month ?? 0, hint: 'first seen this month'},
        {label: 'Stale', value: by.stale ?? 0, hint: 'not seen for 30+ days', color: 'error', status: 'stale'},
    ]
})

const statusHint = (item) => {
    if (item.status === 'idle' && !item.last_usage_date) {
        return 'Seen recently, but this app build has not reported any usage'
    }
    return STATUS[item.status]?.hint || ''
}

const params = () => ({
    search: search.value || undefined,
    app: app.value || undefined,
    platform: platform.value || undefined,
    status: status.value || undefined,
    version: version.value || undefined,
    company_id: companyId.value || undefined,
    user_id: userId.value || undefined,
    include_internal: includeInternal.value ? 1 : undefined,
    include_deleted: includeDeleted.value ? 1 : undefined,
})

const fetchSummary = () => {
    summaryLoading.value = true
    send('get', 'admin/app-installs/summary', params())
        .then((response) => summary.value = response.data.data)
        .catch((error) => snackbar.showSnackbar(error.response?.data?.message || 'Failed to load the summary', 'error'))
        .finally(() => summaryLoading.value = false)
}

let listSeq = 0
const fetchInstalls = () => {
    const seq = ++listSeq
    loading.value = true
    const sort = sortBy.value
    send('get', 'admin/app-installs', {
        ...params(),
        page: page.value,
        per_page: perPage.value,
        sort: sort ? SORT_KEYS[sort.key] : undefined,
        direction: sort ? sort.order : undefined,
    }).then((response) => {
        if (seq !== listSeq) return
        const data = response.data.data
        installs.value = data.data
        totalItems.value = data.total
    }).catch((error) => {
        if (seq !== listSeq) return
        snackbar.showSnackbar(error.response?.data?.message || 'Failed to load app installs', 'error')
    }).finally(() => {
        if (seq === listSeq) loading.value = false
    })
}

const onOptions = (options) => {
    const next = options.sortBy?.[0] || null
    const sortChanged = JSON.stringify(next) !== JSON.stringify(sortBy.value)
    sortBy.value = next
    if (sortChanged && page.value !== 1) {
        page.value = 1
        return
    }
    fetchInstalls()
}

// Filters that change the summary as well as the list.
// Back to page 1. When the page actually changes the table's options event
// fetches, so fetching here too would send the same request twice.
const firstPage = () => {
    if (page.value === 1) {
        fetchInstalls()
    } else {
        page.value = 1
    }
}

const refresh = () => {
    // A version picked under another app would match nothing.
    if (version.value && !versionOptions.value.includes(version.value)) version.value = null
    fetchSummary()
    firstPage()
}

// Status only narrows the list; the summary always shows every status.
const resetFetch = () => firstPage()

const debouncedFetch = () => {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(refresh, 300)
}

const setStatus = (value) => {
    status.value = status.value === value ? null : value
    resetFetch()
}
const setApp = (value) => {
    app.value = value
    refresh()
}
const setPlatform = (value) => {
    platform.value = value
    refresh()
}

const clearScope = (key) => {
    if (key === 'company_id') companyId.value = null
    if (key === 'user_id') userId.value = null
    const query = {...route.query}
    delete query[key]
    router.replace({query})
    refresh()
}

const openRow = (event, {item}) => {
    router.push({name: 'admin-app-install', params: {id: item.id}})
}

fetchSummary()
</script>

